import { useState } from 'react';
import pricingData from '../pricing.json';
import './Pricing.css';

function renderValue(value) {
    if (value === 'Yes') return <span className="value-yes"><i className="fas fa-check"></i></span>;
    if (value === 'No') return <span className="value-no"><i className="fas fa-times"></i></span>;
    return <span className="value-text">{value}</span>;
}

function getPlanIcon(planId) {
    try {
        return require(`../assets/${planId}-icon.png`);
    } catch {
        return null;
    }
}

function formatPrice(price) {
    if (price === null) return null;
    if (typeof price === 'string') return price;
    if (price === 0) return 'Free';
    return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(price);
}

function PlanCta({ plan, className }) {
    return (
        <a
            href={plan.cta.href}
            className={`plan-cta plan-cta--${plan.id} ${className || ''}`}
            target="_blank"
            rel="noopener noreferrer"
        >
            {plan.cta.label}
        </a>
    );
}

/* Desktop: full comparison table */
function PricingTable({ plans, groups, features }) {
    return (
        <div className="pricing-table-wrapper">
            <table className="pricing-table">
                <thead>
                    <tr>
                        <th className="feature-col"></th>
                        {plans.map((plan) => (
                            <th key={plan.id} className={`plan-col plan-col--${plan.id}`}>
                                <div className="plan-header">
                                    <span className="plan-name">
                                        {getPlanIcon(plan.id) && <img src={getPlanIcon(plan.id)} alt="" className="plan-icon" />}
                                        {plan.name}
                                    </span>
                                    <span className="plan-price">
                                        {formatPrice(plan.price_idr)}
                                        {plan.price_idr > 0 && <span className="plan-period">/mo</span>}
                                    </span>
                                    <PlanCta plan={plan} />
                                </div>
                            </th>
                        ))}
                    </tr>
                </thead>
                {groups.map((group) => (
                    <tbody key={group.id}>
                        <tr className="group-row">
                            <td colSpan={plans.length + 1}>{group.label}</td>
                        </tr>
                        {features
                            .filter((feature) => feature.group === group.id)
                            .map((feature) => (
                                <tr key={feature.key}>
                                    <td className="feature-label">{feature.label}</td>
                                    {plans.map((plan) => (
                                        <td key={plan.id} className={`feature-value plan-col--${plan.id}`}>
                                            {renderValue(feature.values[plan.id])}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                    </tbody>
                ))}
            </table>
        </div>
    );
}

/* Mobile: one plan at a time behind a segmented switcher */
function PlanSwitcher({ plans, groups, features }) {
    const [activeId, setActiveId] = useState(plans.find((plan) => plan.featured)?.id || plans[0].id);
    const [showExcluded, setShowExcluded] = useState(false);
    const plan = plans.find((p) => p.id === activeId);
    const excluded = features.filter((feature) => feature.values[plan.id] === 'No');

    const selectPlan = (id) => {
        setActiveId(id);
        setShowExcluded(false);
    };

    return (
        <div className="plan-switcher">
            <div className="plan-tabs" role="tablist">
                {plans.map((p) => (
                    <button
                        key={p.id}
                        role="tab"
                        aria-selected={p.id === activeId}
                        className={`plan-tab plan-tab--${p.id} ${p.id === activeId ? 'is-active' : ''}`}
                        onClick={() => selectPlan(p.id)}
                    >
                        {p.name}
                    </button>
                ))}
            </div>

            <div className={`plan-card plan-card--${plan.id}`} role="tabpanel">
                <div className="plan-card-head">
                    <div className="plan-card-name">
                        {getPlanIcon(plan.id) && <img src={getPlanIcon(plan.id)} alt="" />}
                        {plan.name}
                    </div>
                    {plan.featured && <span className="plan-card-badge">Popular</span>}
                </div>
                <div className="plan-card-price">
                    {plan.price_idr === 0 ? 'Rp 0' : formatPrice(plan.price_idr)}
                    {plan.price_idr > 0 && <span className="plan-period">/mo</span>}
                </div>
                <div className="plan-card-tagline">{plan.tagline}</div>
                <PlanCta plan={plan} className="plan-cta--block" />

                {groups.map((group) => {
                    const rows = features.filter(
                        (feature) => feature.group === group.id && feature.values[plan.id] !== 'No'
                    );
                    if (rows.length === 0) return null;
                    return (
                        <div key={group.id} className="plan-group">
                            <div className="plan-group-label">{group.label}</div>
                            <ul>
                                {rows.map((feature) => {
                                    const value = feature.values[plan.id];
                                    return (
                                        <li key={feature.key} className="plan-feature">
                                            {value === 'Yes' ? (
                                                <>
                                                    <i className="fas fa-check plan-feature-check"></i>
                                                    <span>{feature.label}</span>
                                                </>
                                            ) : (
                                                <>
                                                    <span className="plan-feature-label">{feature.label}</span>
                                                    <span className="plan-feature-value">{value}</span>
                                                </>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    );
                })}

                {excluded.length > 0 && (
                    <div className="plan-excluded">
                        <button
                            className="plan-excluded-toggle"
                            aria-expanded={showExcluded}
                            onClick={() => setShowExcluded(!showExcluded)}
                        >
                            <i className={`fas fa-chevron-${showExcluded ? 'down' : 'right'}`}></i>
                            Not included ({excluded.length})
                        </button>
                        {showExcluded && (
                            <ul>
                                {excluded.map((feature) => (
                                    <li key={feature.key} className="plan-feature plan-feature--excluded">
                                        <i className="fas fa-times"></i>
                                        <span>{feature.label}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function Pricing() {
    const { plans, groups, features } = pricingData;

    return (
        <section className="pricing">
            <div className="pricing-content">
                <h2>Simple, Transparent Pricing</h2>
                <p className="pricing-subtitle">Choose the plan that fits your clinic's needs</p>

                <PricingTable plans={plans} groups={groups} features={features} />
                <PlanSwitcher plans={plans} groups={groups} features={features} />
            </div>
        </section>
    );
}

export default Pricing;
